import type { ComponentType } from 'react';

import {
  FileDocIcon,
  FilePdfIcon,
  FileTextIcon,
  FileXlsIcon,
  type IconProps,
} from '@phosphor-icons/react';

/**
 * Presentation for a document's `standardFormat` — the format the standard
 * (remediated/generated) version of a document is produced as, as curated by
 * the framework catalog.
 *
 * Deliberately distinct from `acceptedFormats` (what a tenant may upload): a
 * document can accept .xlsx on upload yet still be produced as prose, so these
 * two must never be rendered as the same thing.
 */
export interface StandardFormatDisplay {
  /** Uppercased extension without the dot, e.g. `DOCX`. */
  label: string;
  Icon: ComponentType<IconProps>;
  /** Theme palette path used for the badge's text and border. */
  paletteKey: 'info' | 'success' | 'error' | 'secondary';
}

const FORMAT_DISPLAY: Record<string, Omit<StandardFormatDisplay, 'label'>> = {
  docx: { Icon: FileDocIcon, paletteKey: 'info' },
  doc: { Icon: FileDocIcon, paletteKey: 'info' },
  xlsx: { Icon: FileXlsIcon, paletteKey: 'success' },
  xls: { Icon: FileXlsIcon, paletteKey: 'success' },
  pdf: { Icon: FilePdfIcon, paletteKey: 'error' },
};

const FALLBACK: Omit<StandardFormatDisplay, 'label'> = {
  Icon: FileTextIcon,
  paletteKey: 'secondary',
};

/**
 * Resolve the badge shown for a document's standard format.
 *
 * Returns `null` when the catalog carried no standard format for the document,
 * so callers render nothing rather than a misleading placeholder — the seeded
 * catalog may predate the field.
 */
export const getStandardFormatDisplay = (
  standardFormat: string[] | null | undefined,
): StandardFormatDisplay | null => {
  const first = standardFormat?.[0];
  if (!first) return null;

  const extension = first.replace(/^\./, '').toLowerCase();
  if (!extension) return null;

  return { label: extension.toUpperCase(), ...(FORMAT_DISPLAY[extension] ?? FALLBACK) };
};
